/**
 * CostEstimateCard
 *
 * Coverage-aware cost breakdown with line items,
 * insurance coverage visualization, and reduction strategies.
 */

import React, { useState } from "react";
import { View, Text, Pressable } from "react-native";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { MoonStatBlock } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { CostEstimate } from "../intelligence/useCostEstimate";

type Props = {
  estimate: CostEstimate;
  onUploadCoverage?: () => void;
};

function formatDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

const STATUS_CONFIG: Record<
  string,
  { labelKey: string; color: string; bg: string }
> = {
  verified: {
    labelKey: "cost_estimate.coverage_verified",
    color: "#2D7A4F",
    bg: "#2D7A4F18",
  },
  pending: {
    labelKey: "cost_estimate.verification_pending",
    color: "#7A6B2D",
    bg: "#7A6B2D18",
  },
  unverified: {
    labelKey: "cost_estimate.not_yet_verified",
    color: EMBER.colors.textLight,
    bg: `${EMBER.colors.textLight}18`,
  },
  none: {
    labelKey: "cost_estimate.no_coverage_uploaded",
    color: EMBER.colors.textLight,
    bg: `${EMBER.colors.textLight}18`,
  },
};

export default function CostEstimateCard({
  estimate,
  onUploadCoverage,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("CostEstimateCard");
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showStrategies, setShowStrategies] = useState(false);
  const statusConfig = (STATUS_CONFIG[estimate.coverageStatus] ??
    STATUS_CONFIG.none)!;
  const hasCoverage =
    estimate.coverageStatus === "verified" && estimate.totalCoveredCents > 0;

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as object),
        marginTop: 16,
        padding: 16,
      }}
    >
      {/* Header */}
      <View
        style={{
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 12,
        }}
      >
        <View>
          <MoonStatBlock
            label={t("cost_estimate.estimated_cost")}
            value={formatDollars(estimate.totalOutOfPocketCents)}
            layout="label-first"
            caption={
              hasCoverage
                ? t("cost_estimate.of_total", {
                    params: {
                      total: formatDollars(estimate.totalEstimatedCents),
                    },
                  })
                : undefined
            }
          />
        </View>

        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderRadius: 999,
            backgroundColor: statusConfig.bg,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 11,
              fontWeight: "600",
              color: statusConfig.color,
            }}
          >
            {t(statusConfig.labelKey)}
          </Text>
        </View>
      </View>

      {/* Coverage bar */}
      {hasCoverage && (
        <View style={{ marginBottom: 12 }}>
          <View
            style={{
              height: 8,
              borderRadius: 4,
              backgroundColor: `${EMBER.colors.textLight}15`,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                height: "100%",
                width: `${Math.round(
                  (estimate.totalCoveredCents / estimate.totalEstimatedCents) *
                    100,
                )}%`,
                backgroundColor: "#2D7A4F",
                borderRadius: 4,
              }}
            />
          </View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "500",
                color: "#2D7A4F",
              }}
            >
              {t("cost_estimate.insurance_covers", {
                params: {
                  amount: formatDollars(estimate.totalCoveredCents),
                },
              })}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                fontWeight: "500",
                color: EMBER.colors.textLight,
              }}
            >
              {t("cost_estimate.you_pay", {
                params: {
                  amount: formatDollars(estimate.totalOutOfPocketCents),
                },
              })}
            </Text>
          </View>
        </View>
      )}

      {/* Upload coverage CTA if none */}
      {estimate.coverageStatus === "none" && onUploadCoverage && (
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("cost_estimate.upload_to_see")),
              "cost_estimate.upload_to_see",
            );
            onUploadCoverage();
          }}
          style={{
            backgroundColor: `${EMBER.colors.warm}12`,
            borderRadius: 10,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "500",
              color: EMBER.colors.warm,
              textAlign: "center",
            }}
          >
            {t("cost_estimate.upload_to_see")}
          </Text>
        </Pressable>
      )}

      {/* Line item breakdown */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              showBreakdown
                ? t("cost_estimate.hide_breakdown")
                : t("cost_estimate.see_breakdown"),
            ),
            showBreakdown
              ? "cost_estimate.hide_breakdown"
              : "cost_estimate.see_breakdown",
          );
          setShowBreakdown(!showBreakdown);
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: EMBER.colors.warm,
          }}
        >
          {showBreakdown
            ? t("cost_estimate.hide_breakdown")
            : t("cost_estimate.see_breakdown")}
        </Text>
      </Pressable>

      {showBreakdown && (
        <View style={{ marginTop: 10, gap: 6 }}>
          {estimate.lineItems.map((item, idx) => (
            <View
              key={idx}
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingVertical: 6,
                borderBottomWidth: idx < estimate.lineItems.length - 1 ? 1 : 0,
                borderBottomColor: `${EMBER.colors.textLight}10`,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "400",
                  color: EMBER.colors.textLight,
                  flex: 1,
                }}
              >
                {item.label}
              </Text>
              <View style={{ alignItems: "flex-end" }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {formatDollars(item.outOfPocketCents)}
                </Text>
                {item.coveredPercent > 0 && (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 10,
                      fontWeight: "400",
                      color: "#2D7A4F",
                    }}
                  >
                    {t("cost_estimate.percent_covered", {
                      params: { percent: String(item.coveredPercent) },
                    })}
                  </Text>
                )}
              </View>
            </View>
          ))}

          {/* Total */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              paddingTop: 8,
              borderTopWidth: 1,
              borderTopColor: `${EMBER.colors.textLight}20`,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: EMBER.colors.text,
              }}
            >
              {t("cost_estimate.estimated_total")}
            </Text>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "700",
                color: EMBER.colors.text,
              }}
            >
              {formatDollars(estimate.totalOutOfPocketCents)}
            </Text>
          </View>
        </View>
      )}

      {/* Reduction strategies */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              showStrategies
                ? t("cost_estimate.hide_ways_to_reduce")
                : t("cost_estimate.ways_to_reduce"),
            ),
            showStrategies
              ? "cost_estimate.hide_ways_to_reduce"
              : "cost_estimate.ways_to_reduce",
          );
          setShowStrategies(!showStrategies);
        }}
        style={{ marginTop: 10 }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: EMBER.colors.warm,
          }}
        >
          {showStrategies
            ? t("cost_estimate.hide_ways_to_reduce")
            : t("cost_estimate.ways_to_reduce")}
        </Text>
      </Pressable>

      {showStrategies && (
        <View style={{ marginTop: 8, gap: 6 }}>
          {estimate.reductionStrategies.map((strategy, idx) => (
            <View
              key={idx}
              style={{ flexDirection: "row", gap: 8, paddingRight: 8 }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "400",
                  color: EMBER.colors.warm,
                  marginTop: 1,
                }}
              >
                {"\u2022"}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "400",
                  color: EMBER.colors.textLight,
                  lineHeight: 17,
                  flex: 1,
                }}
              >
                {strategy}
              </Text>
            </View>
          ))}
        </View>
      )}

      {/* Disclaimer */}
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          fontWeight: "400",
          color: `${EMBER.colors.textLight}80`,
          marginTop: 12,
          lineHeight: 14,
        }}
      >
        {t("cost_estimate.disclaimer")}
      </Text>
    </View>
  );
}
